import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, TrendingUp, Package, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

const PromotionOptIn = () => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const { promotionId } = useParams();
  
  const [promotion, setPromotion] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [discountInputs, setDiscountInputs] = useState({});

  useEffect(() => {
    fetchData();
  }, [promotionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch promotion details
      const promoResponse = await apiCall(`/api/marketing/promotions/${promotionId}`);
      setPromotion(promoResponse.data);
      
      // Fetch vendor's products (product and rental types)
      const listingsResponse = await apiCall('/api/listings/');
      const vendorProducts = listingsResponse.filter(
        listing => listing.listing_type === 'product' || listing.listing_type === 'rental'
      );
      setProducts(vendorProducts);
    } catch (error) {
      toast.error('Failed to load promotion details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleDiscountChange = (productId, value) => {
    setDiscountInputs(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  const calculateCommittedPrice = (originalPrice, discountPercent) => {
    if (!originalPrice || !discountPercent) return 0;
    return (originalPrice * (1 - discountPercent / 100)).toFixed(2);
  };

  const validateAndSubmit = async () => {
    const selectedProductIds = Object.keys(selectedProducts).filter(id => selectedProducts[id]);
    
    if (selectedProductIds.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    // Validate discounts
    const invalidProducts = [];
    const commitments = [];

    for (const productId of selectedProductIds) {
      const product = products.find(p => p.id === productId);
      const discount = parseFloat(discountInputs[productId] || 0);
      
      if (discount < promotion.discount_mandate_min) {
        invalidProducts.push(`${product.title} (${discount}% < ${promotion.discount_mandate_min}%)`);
        continue;
      }

      const originalPrice = parseFloat(product.data?.price || 0);
      const committedPrice = parseFloat(calculateCommittedPrice(originalPrice, discount));

      commitments.push({
        product_id: product.id,
        product_title: product.title,
        original_price: originalPrice,
        committed_price: committedPrice,
        discount_percentage: discount
      });
    }

    if (invalidProducts.length > 0) {
      toast.error(`Following products don't meet minimum discount: ${invalidProducts.join(', ')}`);
      return;
    }

    if (commitments.length === 0) {
      toast.error('No valid products to commit');
      return;
    }

    // Submit opt-in
    try {
      setSubmitting(true);
      
      await apiCall(`/api/marketing/promotions/${promotionId}/opt-in`, {
        method: 'POST',
        body: JSON.stringify({
          sale_id: promotionId,
          products_committed: commitments
        })
      });

      toast.success('Successfully opted into promotion!');
      navigate('/promotions');
    } catch (error) {
      toast.error(error.message || 'Failed to opt into promotion');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = Object.values(selectedProducts).filter(Boolean).length;
  const avgDiscount = selectedCount > 0
    ? Object.keys(selectedProducts)
        .filter(id => selectedProducts[id])
        .reduce((sum, id) => sum + parseFloat(discountInputs[id] || 0), 0) / selectedCount
    : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading promotion details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!promotion) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-gray-500">Promotion not found</p>
          <Button className="mt-4" onClick={() => navigate('/promotions')}>
            Back to Promotions
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Opt Into Promotion</h1>
            <p className="text-gray-600 mt-2">{promotion.name}</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/promotions')}>
            Cancel
          </Button>
        </div>

        {/* Promotion Details */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <div>
                  <p className="font-medium">Start Date</p>
                  <p>{new Date(promotion.start_timestamp).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <div>
                  <p className="font-medium">End Date</p>
                  <p>{new Date(promotion.end_timestamp).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <TrendingUp className="h-4 w-4" />
                <div>
                  <p className="font-medium">Min Discount Required</p>
                  <p className="text-lg font-bold text-red-600">{promotion.discount_mandate_min}%</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline">{promotion.theme}</Badge>
                <Badge className="bg-blue-100 text-blue-800">{promotion.status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Products Selected</p>
                  <p className="text-2xl font-bold">{selectedCount}</p>
                </div>
                <Package className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Discount</p>
                  <p className="text-2xl font-bold">{avgDiscount.toFixed(1)}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products</p>
                  <p className="text-2xl font-bold">{products.length}</p>
                </div>
                <Package className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Products & Set Discounts</CardTitle>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">You don't have any products to add to this promotion</p>
                <Button className="mt-4" onClick={() => navigate('/listings/products')}>
                  Add Products
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {products.map((product) => {
                  const originalPrice = parseFloat(product.data?.price || 0);
                  const discount = parseFloat(discountInputs[product.id] || 0);
                  const committedPrice = calculateCommittedPrice(originalPrice, discount);
                  const isSelected = selectedProducts[product.id];
                  const meetsMinDiscount = discount >= promotion.discount_mandate_min;

                  return (
                    <Card 
                      key={product.id} 
                      className={`transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : ''}`}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-4">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleProductToggle(product.id)}
                            className="mt-1"
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold">{product.title}</h4>
                                <p className="text-sm text-gray-500">{product.category}</p>
                                <div className="flex items-center gap-4 mt-2">
                                  <span className="text-sm">
                                    Original: <span className="font-semibold">₹{originalPrice}</span>
                                  </span>
                                  {isSelected && discount > 0 && (
                                    <span className="text-sm">
                                      Sale Price: <span className="font-semibold text-green-600">₹{committedPrice}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="w-48">
                                <Label className="text-xs">Discount %</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min={promotion.discount_mandate_min}
                                  max="100"
                                  value={discountInputs[product.id] || ''}
                                  onChange={(e) => handleDiscountChange(product.id, e.target.value)}
                                  placeholder={`Min ${promotion.discount_mandate_min}%`}
                                  disabled={!isSelected}
                                  className={!isSelected ? 'bg-gray-100' : ''}
                                />
                                {isSelected && discount > 0 && (
                                  <div className="mt-1">
                                    {meetsMinDiscount ? (
                                      <div className="flex items-center gap-1 text-xs text-green-600">
                                        <CheckCircle2 className="h-3 w-3" />
                                        Valid discount
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 text-xs text-red-600">
                                        <AlertCircle className="h-3 w-3" />
                                        Below minimum
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {products.length > 0 && (
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={() => navigate('/promotions')}>
              Cancel
            </Button>
            <Button
              onClick={validateAndSubmit}
              disabled={selectedCount === 0 || submitting}
            >
              {submitting ? 'Submitting...' : `Opt In with ${selectedCount} Product${selectedCount !== 1 ? 's' : ''}`}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PromotionOptIn;
